import { corroborated, observed, provisional, sourced } from './provenance';
import behavioralData from '../../../../reference/canyon-defense-1/evidence/behavioral-data.json';
import type {
  CatalogueId,
  BuildingDefinition,
  BuildingId,
  DifficultyId,
  Domain,
  EnemyDefinition,
  EnemySize,
  MapDefinition,
  MapId,
  TowerId,
  TowerDefinition,
  TunedValue,
  WaveDefinition,
  WaveEffect,
  WaveGroup,
} from './types';

// Decompiled SWF constants: the visual 650x450 stage contains a 448x448 logical battlefield.
export const LOGICAL_BOARD_SIZE = 448;
export const CELL_SIZE = 32;
export const GRID_SIZE = 14;
export const FIXED_TICK_HZ = 24;

const authored = (value: number, note: string): TunedValue => ({ value, provenance: provisional(note) });

export const DIFFICULTIES: Record<DifficultyId, {
  label: string;
  waves: number;
  interval: string;
  bonus: string;
  waveIntervalTicks: TunedValue;
  startingCash: TunedValue;
  startingLives: TunedValue;
  scoreMultiplier: TunedValue;
}> = {
  easy: {
    label: 'Easy', waves: 30, interval: '2:00', bonus: '0%',
    waveIntervalTicks: { value: 2880, provenance: observed(['CD1-SWF-CGame'], 'Easy automatic wave interval is 2,880 original frames.') },
    startingCash: { value: 350, provenance: observed(['CD1-CAP-004', 'CD1-ECO-001'], 'Captured Map 1 Easy starting cash.') },
    startingLives: { value: 10, provenance: observed(['CD1-CAP-004', 'CD1-CAP-010', 'CD1-CAP-011'], 'Captured Easy starting lives.') },
    scoreMultiplier: { value: 1, provenance: observed(['CD1-SWF-CGame'], 'Easy score bonus is 0%.') },
  },
  normal: {
    label: 'Normal', waves: 40, interval: '1:30', bonus: '25%',
    waveIntervalTicks: { value: 2160, provenance: observed(['CD1-SWF-CGame'], 'Normal automatic wave interval is 2,160 original frames.') },
    startingCash: { value: 350, provenance: observed(['CD1-SWF-CGame'], 'Original session cash is shared across difficulties.') },
    startingLives: { value: 10, provenance: observed(['CD1-SWF-CGame'], 'Original session population is shared across difficulties.') },
    scoreMultiplier: { value: 1.25, provenance: observed(['CD1-SWF-CGame'], 'Normal score bonus is 25%.') },
  },
  hard: {
    label: 'Hard', waves: 50, interval: '1:00', bonus: '50%',
    waveIntervalTicks: { value: 1440, provenance: observed(['CD1-SWF-CGame'], 'Hard automatic wave interval is 1,440 original frames.') },
    startingCash: { value: 350, provenance: observed(['CD1-SWF-CGame'], 'Original session cash is shared across difficulties.') },
    startingLives: { value: 10, provenance: observed(['CD1-SWF-CGame'], 'Original session population is shared across difficulties.') },
    scoreMultiplier: { value: 1.5, provenance: observed(['CD1-SWF-CGame'], 'Hard score bonus is 50%.') },
  },
};

export const STARTING_CASH = DIFFICULTIES.easy.startingCash;
export const STARTING_LIVES = DIFFICULTIES.easy.startingLives;
export const SELL_FRACTION = {
  value: 0.5,
  provenance: observed(['CD1-ECO-004'], 'A $100 Missile Turret sold for $50.'),
};
export const LEAK_LIFE_DAMAGE: TunedValue = {
  value: 1,
  provenance: corroborated(['S01', 'S04'], 'Each escaping unit costs one life.'),
};
export const PROJECTILE_HIT_RADIUS: TunedValue = authored(7, 'Unmeasured projectile collision radius; authored vertical-slice tolerance.');
export const SCORE_PER_REWARD: TunedValue = {
  value: 1,
  provenance: observed(['CD1-SWF-CGame'], 'Money contributes one-for-one to score before the end-of-run life score and difficulty bonus.'),
};
export const TOWER_FIRE_ALIGNMENT_RADIANS: TunedValue = authored(0.18, 'Unmeasured fire-alignment tolerance; authored vertical-slice aiming rule.');
export const CHAIN_HOP_RADIUS = 48;

type TowerSpec = {
  id: TowerId; label: string; shortLabel: string; cost: number; domains: readonly Domain[];
  range: number; turnPerFrame: number; reloadFrames: number; projectile: TowerDefinition['projectile']; speedPerFrame: number;
};

const towerSpecs: readonly TowerSpec[] = [
  { id: 'cd1.tower.missile', label: 'Missile Turret', shortLabel: 'M-TUR', cost: 100, domains: ['ground'], range: 100, turnPerFrame: 0.1, reloadFrames: 36, projectile: 'rocket', speedPerFrame: 3 },
  { id: 'cd1.tower.howitzer', label: 'Howitzer', shortLabel: 'HOWITZ', cost: 200, domains: ['ground'], range: 200, turnPerFrame: 0.05, reloadFrames: 108, projectile: 'ball', speedPerFrame: 3 },
  { id: 'cd1.tower.tesla', label: 'Tesla Tower', shortLabel: 'TESLA', cost: 500, domains: ['ground'], range: 60, turnPerFrame: 0, reloadFrames: 48, projectile: 'electricField', speedPerFrame: 0 },
  { id: 'cd1.tower.gatling', label: 'Gatling Laser', shortLabel: 'G-LASR', cost: 800, domains: ['ground'], range: 70, turnPerFrame: 0.1, reloadFrames: 6, projectile: 'laserBeam', speedPerFrame: 0 },
  { id: 'cd1.tower.nulificator', label: 'Nulificator', shortLabel: 'NULL', cost: 1000, domains: ['ground', 'air'], range: 100, turnPerFrame: 0.1, reloadFrames: 250, projectile: 'nulifyBall', speedPerFrame: 4 },
  { id: 'cd1.tower.aa', label: 'AA Gun', shortLabel: 'AA-GUN', cost: 150, domains: ['air'], range: 110, turnPerFrame: 0.1, reloadFrames: 6, projectile: 'aaShell', speedPerFrame: 0 },
  { id: 'cd1.tower.goo', label: 'Goo Cannon', shortLabel: 'GOO', cost: 200, domains: ['ground', 'air'], range: 90, turnPerFrame: 0.1, reloadFrames: 24, projectile: 'slowShot', speedPerFrame: 4 },
  { id: 'cd1.tower.chain', label: 'Chain Gun', shortLabel: 'CHAIN', cost: 500, domains: ['ground'], range: 100, turnPerFrame: 0.1, reloadFrames: 24, projectile: 'chainBlast', speedPerFrame: 7 },
  { id: 'cd1.tower.gauss', label: 'Gauss Cannon', shortLabel: 'GAUSS', cost: 800, domains: ['air'], range: 90, turnPerFrame: 0.1, reloadFrames: 24, projectile: 'gaussRail', speedPerFrame: 0 },
  { id: 'cd1.tower.oil', label: 'Oil Spray', shortLabel: 'OIL', cost: 1000, domains: ['ground'], range: 120, turnPerFrame: 0.1, reloadFrames: 24, projectile: 'confuseBall', speedPerFrame: 5 },
];

type ProjectileRule = Pick<TowerDefinition, 'damage' | 'splashRadius' | 'piercesShield' | 'slow' | 'confuse' | 'clearsEffects' | 'chainTargets' | 'chainRadius'> & { turnPerFrame: number };

/** Recovered behavioral reference; retained for evidence, not used by the authored runtime balance. */
export const REFERENCE_MISSILE_DAMAGE: TunedValue = {
  value: 25,
  provenance: observed(['CD1-SWF-CBlast'], 'Recovered Silo missile damage reference is 25.'),
};

/** User-directed balance override: it intentionally replaces the recovered 25-damage runtime value. */
export const AUTHORED_MISSILE_DAMAGE: TunedValue = {
  value: 50,
  provenance: {
    status: 'AUTHORED',
    sourceIds: ['USER-DIRECTED-MISSILE-BALANCE'],
    note: 'Canyon Defense+ base Missile ability deals 50 shield-piercing damage; recovered reference remains 25.',
  },
};

const projectileRules: Record<TowerDefinition['projectile'], ProjectileRule> = {
  rocket: { damage: { value: 5, provenance: observed(['CD1-SWF-CBlast'], 'Rocket damage is 5.') }, splashRadius: 0, turnPerFrame: 0.2 },
  cruiseMissile: { damage: { value: 5, provenance: observed(['CD1-SWF-CBlast'], 'Cruise Missile retains the recovered homing rocket behavior.') }, splashRadius: 0, turnPerFrame: 0.2 },
  ball: { damage: { value: 20, provenance: observed(['CD1-SWF-CBlast'], 'Howitzer ball damage is 20.') }, splashRadius: 25, turnPerFrame: 0.05 },
  mortarShell: { damage: { value: 20, provenance: observed(['CD1-SWF-CBlast'], 'Mortar retains the recovered fixed-point Howitzer shell behavior.') }, splashRadius: 25, turnPerFrame: 0.05 },
  electricField: { damage: { value: 10, provenance: observed(['CD1-SWF-CBlast'], 'Tesla field damage is 10.') }, splashRadius: 60, turnPerFrame: 0 },
  laserBeam: { damage: { value: 2, provenance: observed(['CD1-SWF-CBlast'], 'Laser beam damage is 2.') }, splashRadius: 0, piercesShield: true, turnPerFrame: 0 },
  nulifyBall: { damage: { value: 0, provenance: observed(['CD1-SWF-CBlast'], 'Nulify Ball clears enemy effects.') }, splashRadius: 0, clearsEffects: true, turnPerFrame: 0.1 },
  aaShell: { damage: { value: 1, provenance: observed(['CD1-SWF-CBlast'], 'AA Shell damage is 1.') }, splashRadius: 0, turnPerFrame: 0 },
  slowShot: { damage: { value: 1, provenance: observed(['CD1-SWF-CBlast'], 'Slow Shot damage is 1.') }, splashRadius: 15, slow: { percent: 0.25, ticks: 36 }, turnPerFrame: 0.1 },
  chainBlast: { damage: { value: 8, provenance: observed(['CD1-SWF-CBlast'], 'Chain Blast damage is 8.') }, splashRadius: 0, chainTargets: 3, chainRadius: CHAIN_HOP_RADIUS, turnPerFrame: Math.PI },
  gaussRail: { damage: { value: 8, provenance: observed(['CD1-SWF-CBlast'], 'Gauss Rail damage is 8.') }, splashRadius: 0, piercesShield: true, turnPerFrame: 0 },
  confuseBall: { damage: { value: 3, provenance: observed(['CD1-SWF-CBlast'], 'Confuse Ball damage is 3.') }, splashRadius: 0, confuse: { percent: 0.7, ticks: 120 }, turnPerFrame: 0.5 },
  missile: { damage: AUTHORED_MISSILE_DAMAGE, splashRadius: 100, piercesShield: true, turnPerFrame: 0.1 },
};

export const TOWERS: Record<TowerId, TowerDefinition> = Object.fromEntries(towerSpecs.map((spec, index) => {
  const effects = projectileRules[spec.projectile];
  return [spec.id, {
    id: spec.id, researchId: `CD1-TWR-${(index + 1).toString().padStart(2, '0')}`,
    label: spec.label, shortLabel: spec.shortLabel,
    cost: { value: spec.cost, provenance: observed(['CD1-SWF-Catalogue'], 'Exact catalogue price extracted from the supplied SWF behavioral dataset.') },
    domains: spec.domains,
    range: { value: spec.range, provenance: observed(['CD1-SWF-CBuilding'], 'Exact original tower range.') },
    damage: effects.damage,
    reloadTicks: { value: spec.reloadFrames, provenance: observed(['CD1-SWF-CBuilding'], 'Exact original reload in 24fps frames.') },
    projectileSpeed: { value: spec.speedPerFrame * FIXED_TICK_HZ, provenance: observed(['CD1-SWF-CBuilding'], 'Exact projectile speed converted from original pixels per frame; zero means instantaneous target-distance travel.') },
    turnRate: { value: spec.turnPerFrame * FIXED_TICK_HZ, provenance: observed(['CD1-SWF-CBuilding'], 'Exact turning rate converted from original radians per frame.') },
    projectileTurnRate: { value: effects.turnPerFrame, provenance: observed(['CD1-SWF-CBlast'], 'Exact projectile steering in original radians per frame.') },
    projectile: spec.projectile, splashRadius: effects.splashRadius,
    ...(effects.piercesShield ? { piercesShield: true } : {}), ...(effects.slow ? { slow: effects.slow } : {}), ...(effects.confuse ? { confuse: effects.confuse } : {}), ...(effects.clearsEffects ? { clearsEffects: true } : {}), ...(effects.chainTargets ? { chainTargets: effects.chainTargets } : {}), ...(effects.chainRadius ? { chainRadius: effects.chainRadius } : {}),
    placement: observed(['CD1-SWF-CMap'], 'Tower construction uses the recovered 1 by 1 cell footprint.'),
  } satisfies TowerDefinition];
})) as Record<TowerId, TowerDefinition>;

const buildingSpecs: readonly { id: BuildingId; label: string; shortLabel: string; cost: number }[] = [
  { id: 'cd1.building.silo', label: 'Nuclear Silo', shortLabel: 'SILO', cost: 500 },
  { id: 'cd1.building.wall-factory', label: 'Wall Factory', shortLabel: 'WALL', cost: 800 },
  { id: 'cd1.building.recycler', label: 'Recycler', shortLabel: 'RCLYR', cost: 900 },
  { id: 'cd1.building.radar', label: 'Radar Dome', shortLabel: 'RADAR', cost: 1200 },
  { id: 'cd1.building.laboratory', label: 'Laboratory', shortLabel: 'LAB', cost: 1250 },
];

export const BUILDINGS: Record<BuildingId, BuildingDefinition> = Object.fromEntries(buildingSpecs.map((spec) => [spec.id, {
  ...spec,
  cost: { value: spec.cost, provenance: observed(['CD1-SWF-Catalogue'], 'Exact catalogue price extracted from the supplied SWF behavioral dataset.') },
  placement: observed(['CD1-SWF-CMap'], 'Buildings use the recovered 2 by 2 cell footprint.'),
} satisfies BuildingDefinition])) as Record<BuildingId, BuildingDefinition>;

export const CATALOGUE: readonly {
  id: CatalogueId;
  tab: 'basic' | 'advanced' | 'buildings';
  label: string;
  cost: number;
  domains: string;
  description: string;
  requirement: string;
  playable: boolean;
  provenance: string;
}[] = [
  { id: 'cd1.tower.missile', tab: 'basic', label: 'Missile Turret', cost: 100, domains: 'GROUND', description: 'Ground · 5 damage · 100 range · homing', requirement: 'READY', playable: true, provenance: 'OBSERVED' },
  { id: 'cd1.tower.howitzer', tab: 'basic', label: 'Howitzer', cost: 200, domains: 'GROUND', description: 'Ground · 20 damage · 25 splash · 200 range', requirement: 'Use Missile ability', playable: false, provenance: 'OBSERVED' },
  { id: 'cd1.tower.tesla', tab: 'basic', label: 'Tesla Tower', cost: 500, domains: 'GROUND', description: 'Ground pulse · 10 damage · 60 radius', requirement: 'Build 5 Goo Cannons', playable: false, provenance: 'CORROBORATED' },
  { id: 'cd1.tower.gatling', tab: 'basic', label: 'Gatling Laser', cost: 800, domains: 'GROUND', description: 'Ground · 2 damage · pierces shields · rapid · 70 range', requirement: 'Destroy 40 flying enemies', playable: false, provenance: 'CORROBORATED' },
  { id: 'cd1.tower.nulificator', tab: 'basic', label: 'Nulificator', cost: 1000, domains: 'ALL', description: 'All targets · clears effects · 100 range', requirement: 'Build Laboratory', playable: false, provenance: 'OBSERVED' },
  { id: 'cd1.tower.aa', tab: 'advanced', label: 'AA Gun', cost: 150, domains: 'AIR', description: 'Air · 1 damage · rapid · 110 range', requirement: 'READY', playable: true, provenance: 'CORROBORATED' },
  { id: 'cd1.tower.goo', tab: 'advanced', label: 'Goo Cannon', cost: 200, domains: 'GROUND / AIR', description: 'All targets · slows 25% · 15 splash · 90 range', requirement: 'Destroy 5 air units', playable: false, provenance: 'OBSERVED' },
  { id: 'cd1.tower.chain', tab: 'advanced', label: 'Chain Gun', cost: 500, domains: 'GROUND', description: 'Ground · 8 damage · chains 3 · 100 range', requirement: 'Hold $1,000', playable: false, provenance: 'OBSERVED' },
  { id: 'cd1.tower.gauss', tab: 'advanced', label: 'Gauss Cannon', cost: 800, domains: 'AIR', description: 'Air · 8 damage · pierces shields · 90 range', requirement: 'Build Radar Dome', playable: false, provenance: 'OBSERVED' },
  { id: 'cd1.tower.oil', tab: 'advanced', label: 'Oil Spray', cost: 1000, domains: 'GROUND', description: 'Ground · 3 damage · turning -70% · 120 range', requirement: 'Build 10 Walls', playable: false, provenance: 'OBSERVED' },
  { id: 'cd1.building.silo', tab: 'buildings', label: 'Nuclear Silo', cost: 500, domains: 'ACTION', description: 'Missile source · recharge +1 per tick each', requirement: 'Build 5 Missile Turrets', playable: false, provenance: 'OBSERVED' },
  { id: 'cd1.building.wall-factory', tab: 'buildings', label: 'Wall Factory', cost: 800, domains: 'ACTION', description: 'Wall source · recharge +1 per tick each', requirement: 'Build 15 structures', playable: false, provenance: 'OBSERVED' },
  { id: 'cd1.building.recycler', tab: 'buildings', label: 'Recycler', cost: 900, domains: 'SUPPORT', description: 'Support · kill credits +50% each', requirement: 'Build 3 Tesla/Chain', playable: false, provenance: 'OBSERVED' },
  { id: 'cd1.building.radar', tab: 'buildings', label: 'Radar Dome', cost: 1200, domains: 'SUPPORT', description: 'Support · tower range +10% each', requirement: 'Build 7 AA Guns', playable: false, provenance: 'OBSERVED' },
  { id: 'cd1.building.laboratory', tab: 'buildings', label: 'Laboratory', cost: 1250, domains: 'ACTION', description: 'Stop Time source · recharge +1 per tick each', requirement: 'Build 2 Wall Factories', playable: false, provenance: 'OBSERVED' },
];

/** Exact construction hit-test footprint in 32px battlefield cells. */
export const STRUCTURE_FOOTPRINT_CELLS: Record<CatalogueId, readonly [width: number, height: number]> = {
  'cd1.tower.missile': [1, 1],
  'cd1.tower.howitzer': [1, 1],
  'cd1.tower.tesla': [1, 1],
  'cd1.tower.gatling': [1, 1],
  'cd1.tower.nulificator': [1, 1],
  'cd1.tower.aa': [1, 1],
  'cd1.tower.goo': [1, 1],
  'cd1.tower.chain': [1, 1],
  'cd1.tower.gauss': [1, 1],
  'cd1.tower.oil': [1, 1],
  'cd1.building.silo': [2, 2],
  'cd1.building.wall-factory': [2, 2],
  'cd1.building.recycler': [2, 2],
  'cd1.building.radar': [2, 2],
  'cd1.building.laboratory': [2, 2],
};

export const ACTIVE_COOLDOWN_TICKS = 5400;

/** Shared simulation and view targeting facts for the three active abilities. */
export const ABILITY_RULES = {
  missile: { radius: 100, launch: { x: 224, y: 500 }, speedPerTick: 6 * FIXED_TICK_HZ, damage: AUTHORED_MISSILE_DAMAGE },
  wall: { requiresInteriorRouteCell: true },
  'stop-time': { radius: 100, durationTicks: 120 },
  mine: { damage: 50, radius: 35, fuseTicks: FIXED_TICK_HZ },
  'barbed-wire': {
    uses: 20,
    slowBySize: { tiny: 0.75, small: 0.5, medium: 0.25, big: 0 },
    useCostBySize: { tiny: 1, small: 2, medium: 4, big: 20 },
  },
} as const;

export const isTowerId = (id: CatalogueId): id is TowerId => id.startsWith('cd1.tower.');
export const isBuildingId = (id: CatalogueId): id is BuildingId => id.startsWith('cd1.building.');
export const structureLabel = (id: CatalogueId): string => isTowerId(id) ? TOWERS[id].label : BUILDINGS[id].label;

const cell = (x: number, y: number) => `${x},${y}`;
const roadsFromSurfaceTrace = (rows: readonly string[], forceRoad: readonly [number, number][] = [], forceTerrain: readonly [number, number][] = []): string[] => {
  const roads = new Set<string>();
  rows.forEach((row, y) => [...row].forEach((surface, x) => { if (surface === 'S') roads.add(cell(x, y)); }));
  forceRoad.forEach(([x, y]) => roads.add(cell(x, y)));
  forceTerrain.forEach(([x, y]) => roads.delete(cell(x, y)));
  return [...roads];
};

const hitMaskToSurface = (rows: readonly string[]): string[] => rows.map((row) => row.replaceAll('#', 'T').replaceAll('.', 'S'));

// Exact 14x14 hit-test masks from the original matrix sprites: # rock/build terrain, . route.
const exactRockMasks = behavioralData.maps.rockMasks as unknown as Record<MapId, readonly string[]>;
const MAP_1_SURFACE = hitMaskToSurface(exactRockMasks['map-1']);
const MAP_2_SURFACE = hitMaskToSurface(exactRockMasks['map-2']);
const MAP_3_SURFACE = hitMaskToSurface(exactRockMasks['map-3']);

export const MAPS: Record<MapId, MapDefinition> = {
  'map-1': {
    id: 'map-1', label: 'Canyon One', descriptor: 'Looping canyon route',
    surfaceRows: MAP_1_SURFACE,
    roadCells: roadsFromSurfaceTrace(MAP_1_SURFACE),
    palette: { terrain: 0xe8d6a5, rock: 0x805029, route: 0xf2e3bd, routeEdge: 0x9c6a39, accent: 0x4f9ec5 },
    existence: observed(['CD1-CAP-004', 'CD1-CAP-017'], 'Map 1 selection and fresh board are observed.'),
    geometry: observed(['CD1-SWF-CMap'], 'Exact 14 by 14 hit-test mask and original navigation rules are extracted from the supplied SWF behavioral dataset.'),
  },
  'map-2': {
    id: 'map-2', label: 'Canyon Two', descriptor: 'Zig-zag shelf pass',
    surfaceRows: MAP_2_SURFACE,
    roadCells: roadsFromSurfaceTrace(MAP_2_SURFACE),
    palette: { terrain: 0xe9d7a9, rock: 0x765033, route: 0xf3e4bd, routeEdge: 0x8f6b44, accent: 0x4f9ec5 },
    existence: observed(['CD1-CAP-010', 'CD1-CAP-018'], 'Map 2 selection and fresh board are observed.'),
    geometry: observed(['CD1-SWF-CMap'], 'Exact 14 by 14 hit-test mask and original navigation rules are extracted from the supplied SWF behavioral dataset.'),
  },
  'map-3': {
    id: 'map-3', label: 'Canyon Three', descriptor: 'Vertical canyon throat',
    surfaceRows: MAP_3_SURFACE,
    roadCells: roadsFromSurfaceTrace(MAP_3_SURFACE),
    palette: { terrain: 0xe6d4a6, rock: 0x724a2f, route: 0xf1e0b6, routeEdge: 0x786d60, accent: 0x4f9ec5 },
    existence: observed(['CD1-CAP-011', 'CD1-CAP-019'], 'Map 3 selection and fresh board are observed.'),
    geometry: observed(['CD1-SWF-CMap'], 'Exact 14 by 14 hit-test mask and original navigation rules are extracted from the supplied SWF behavioral dataset.'),
  },
};

const baseReward: Record<Domain, Record<EnemySize, number>> = {
  ground: { tiny: 20, small: 50, medium: 85, big: 120 },
  air: { tiny: 30, small: 75, medium: 100, big: 100 },
};
const baseHp: Record<Domain, Record<EnemySize, number>> = {
  ground: { tiny: 15, small: 35, medium: 70, big: 120 },
  air: { tiny: 8, small: 20, medium: 40, big: 40 },
};
const baseSpeedPerFrame: Record<Domain, Record<EnemySize, number>> = {
  ground: { tiny: 2, small: 1.7, medium: 1.5, big: 1.1 },
  air: { tiny: 1.8, small: 1.5, medium: 1.2, big: 1.2 },
};
const baseThrottlePerFrame: Record<Domain, Record<EnemySize, number>> = {
  ground: { tiny: 0.3, small: 0.3, medium: 0.25, big: 0.2 },
  air: { tiny: 0.3, small: 0.3, medium: 0.3, big: 0.3 },
};
const baseTurnPerFrame: Record<Domain, Record<EnemySize, number>> = {
  ground: { tiny: 0.11, small: 0.1, medium: 0.08, big: 0.06 },
  air: { tiny: 0.11, small: 0.16, medium: 0.06, big: 0.06 },
};

export function enemyDefinition(domain: Domain, size: EnemySize, level: number): EnemyDefinition {
  const reward = baseReward[domain][size] * (1 + 0.5 * (level - 1));
  const isFirstKill = domain === 'ground' && size === 'tiny' && level === 1;
  const hp = baseHp[domain][size] + Math.round(1.2 * (level - 1) * baseHp[domain][size]);
  return {
    id: `cd1.enemy.${domain}.${size}.l${level}`,
    domain, size, level,
    reward: {
      value: reward,
      provenance: isFirstKill
        ? observed(['CD1-ECO-003'], 'First Map 1 Easy Tiny Ground L1 reward.')
        : observed(['CD1-SWF-CEnemy'], 'Base reward and level multiplier are extracted original behavioral constants.'),
    },
    hp: { value: hp, provenance: observed(['CD1-SWF-CEnemy'], 'Base HP plus the original rounded 120% per-level increment.') },
    speed: { value: baseSpeedPerFrame[domain][size] * FIXED_TICK_HZ, provenance: observed(['CD1-SWF-CEnemy'], 'Original maximum speed is measured in pixels per 24fps frame.') },
    throttlePerTick: { value: baseThrottlePerFrame[domain][size] * FIXED_TICK_HZ, provenance: observed(['CD1-SWF-CEnemy'], 'Original per-frame throttle converted to the fixed 24fps simulation.') },
    turnPerTick: { value: baseTurnPerFrame[domain][size], provenance: observed(['CD1-SWF-CEnemy'], 'Original per-frame turning limit; one simulation tick is one original frame.') },
  };
}

const group = (domain: Domain, size: EnemySize, level: number, count: number, composition: WaveGroup['composition'], effect?: WaveEffect): WaveGroup => ({ domain, size, level, count, composition, effect });
const wave = (number: number, groups: readonly WaveGroup[]): WaveDefinition => ({
  id: `cd1.wave.${number.toString().padStart(2, '0')}`,
  number,
  groups,
  spawnEveryTicks: { value: 0, provenance: observed(['CD1-SWF-CGame'], 'Each original program row creates its complete unit group together.') },
});

type BehavioralWave = { wave: number; domain: Domain; count: number; size: EnemySize; level: number; effect?: WaveEffect };

const sourcePrograms = behavioralData.waves as unknown as Record<DifficultyId, readonly BehavioralWave[]>;
const observedComposition = (difficulty: DifficultyId, row: BehavioralWave): WaveGroup['composition'] => {
  if (difficulty === 'easy' && row.wave === 1) return observed(['CD1-CAP-004', 'CD1-SWF-CGame'], 'Map 1 Easy preview and extracted program row agree.');
  if (difficulty === 'easy' && row.wave === 2) return observed(['CD1-CAP-006', 'CD1-SWF-CGame'], 'Map 1 Easy preview and extracted program row agree.');
  if (difficulty === 'easy' && row.wave === 3) return observed(['CD1-CAP-023', 'CD1-SWF-CGame'], 'Map 1 Easy preview and extracted program row agree.');
  return sourced(['CD1-SWF-CGame'], 'Exact program row extracted from the supplied SWF behavioral dataset.');
};
const programRows = (difficulty: DifficultyId): readonly WaveDefinition[] => sourcePrograms[difficulty].map((row) => wave(row.wave, [
  group(row.domain, row.size, row.level, row.count, observedComposition(difficulty, row), row.effect),
]));
const exactPrograms: Record<DifficultyId, readonly WaveDefinition[]> = {
  easy: programRows('easy'),
  normal: programRows('normal'),
  hard: programRows('hard'),
};

// Program choice depends on difficulty, not map; all scenarios therefore share these exact extracted rows.
export const WAVE_SCRIPTS: Record<MapId, Record<DifficultyId, readonly WaveDefinition[]>> = {
  'map-1': exactPrograms,
  'map-2': exactPrograms,
  'map-3': exactPrograms,
};

export const abilityRequirement = (ability: 'sell' | 'pulse' | 'barrier' | 'stasis', missileCount: number): string => {
  if (ability === 'sell') return 'READY · 50% refund';
  if (ability === 'pulse') return missileCount >= 5 ? 'Silo chain available (slice gate)' : `${missileCount}/5 Missile Turrets`;
  if (ability === 'barrier') return 'LOCKED · Build 15 structures';
  return 'LOCKED · Build 2 Wall Factories';
};

export const isKnownCatalogueId = (id: string): id is CatalogueId => CATALOGUE.some((entry) => entry.id === id);
